import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Document, Page } from 'react-pdf';
import { pdfjs } from 'react-pdf';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import LeftArrow from '@mui/icons-material/ArrowBackIosNewRounded';
import RightArrow from '@mui/icons-material/ArrowForwardIosRounded';
import ZoomIn from '@mui/icons-material/ZoomInRounded';
import ZoomOut from '@mui/icons-material/ZoomOutRounded';
import AttachmentIcon from '@mui/icons-material/AttachmentRounded';
import ModalDialog from '../../Dialog/ModalDialog';

// Bundled same-origin instead of fetched from unpkg.com - CSP's worker-src only
// allows 'self'/blob:, and a cross-origin CDN worker is also a production
// reliability risk this security-hardened app shouldn't depend on regardless.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
).toString();


export default function AttachmentViewer(props) {
    const { t } = useTranslation('translation');
    const { attachment, open, toggleDialog } = props;
    const [record, setRecord] = useState('');
    const [pageNumber, setPageNumber] = useState(1);
    const [numPages, setNumPages] = useState(1);
    const [scale, setScale] = useState(1.4);

    const attachmentId = attachment?._id;
    useEffect(() => {
        if (attachmentId == null)
            return;
        const controller = new AbortController();
        setRecord('');
        setPageNumber(1);
        (async () => {
            try {
                const data = await axios.get('/api/document/get/' + attachmentId, { signal: controller.signal });
                setRecord(data.data);
            } catch (error) {
                if (!axios.isCancel(error))
                    console.error('Error loading PDF:', error);
            }
        })();
        return () => controller.abort();
    }, [attachmentId]);

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
    };

    const handleTraverse = (direction) => {
        setPageNumber(prev => direction === 'next' ? prev + 1 : prev - 1);
    };

    const adjustScale = (direction) => {
        setScale(prev => direction === 'in' ? prev + 0.2 : prev - 0.2);
    };

    const footerControls = (
        <Grid
            container
            spacing={0}
            direction='row'
            justifyContent='space-between'
            sx={{ width: '100%' }}
        >
            <Grid size={1}>
                <IconButton
                    aria-label='zoom out'
                    onClick={() => adjustScale('out')}
                    disabled={scale <= 0.4}
                >
                    <ZoomOut fontSize='large' />
                </IconButton>
            </Grid>
            <Grid sx={{ textAlign: 'center' }} size={10}>
                <IconButton
                    aria-label='previous page'
                    onClick={() => handleTraverse('prev')}
                    disabled={pageNumber <= 1}
                >
                    <LeftArrow />
                </IconButton>
                <span>{t('page')} {pageNumber} {t('of')} {numPages}</span>
                <IconButton
                    aria-label='next page'
                    onClick={() => handleTraverse('next')}
                    disabled={pageNumber >= numPages}
                >
                    <RightArrow />
                </IconButton>
            </Grid>
            <Grid sx={{ textAlign: 'right' }} size={1}>
                <IconButton
                    aria-label='zoom in'
                    onClick={() => adjustScale('in')}
                    disabled={scale >= 4.0}
                >
                    <ZoomIn fontSize='large' />
                </IconButton>
            </Grid>
        </Grid>
    );

    return (
        <ModalDialog
            dialogStatus={open}
            toggleDialog={toggleDialog}
            title={attachment.name}
            icon={<AttachmentIcon />}
            downloadRecord={attachment}
            fullWidth
            maxWidth='md'
            buttonStack={footerControls}
        >
            {record && (attachment.type === 'application/pdf' ? (
                <Document
                    file={'data:application/octet-stream;base64,' + record}
                    onLoadSuccess={onDocumentLoadSuccess}
                >
                    <Page
                        pageNumber={pageNumber}
                        scale={scale}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                    />
                </Document>
            ) : (
                <img
                    src={'data:application/octet-stream;base64,' + record}
                    alt={attachment.name ?? ''}
                    height='auto'
                    style={{ width: `${(scale / 1.4) * 100}%`, display: 'block', margin: '0 auto' }}
                />
            ))}
        </ModalDialog>
    );
}
